import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Reveal, OceanTag, StatusChip } from "./kit";
import { OCEAN_REGIONS, MAP_MARKERS, REGION_COLORS } from "../../data/oceanContent";

const REGION_VIEW = {
  west: { center: [15, 73], zoom: 5 },
  east: { center: [15.5, 82.5], zoom: 5 },
  andaman: { center: [11.9, 92.95], zoom: 7 },
  offshore: { center: [14, 78], zoom: 5 },
};

export default function RegionsMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(OCEAN_REGIONS[0]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    try {
      const map = L.map(containerRef.current, {
        scrollWheelZoom: false, zoomControl: true, attributionControl: true, minZoom: 3, maxZoom: 9,
      }).setView([15.5, 80], 4);
      mapRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      const seaLabel = (lat, lng, text) =>
        L.marker([lat, lng], {
          interactive: false,
          icon: L.divIcon({
            className: "",
            html: `<span style="color:#9fd9d6;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:2px;text-shadow:0 0 6px #06182A">${text}</span>`,
          }),
        }).addTo(map);
      seaLabel(15, 66, "ARABIAN SEA");
      seaLabel(14, 88, "BAY OF BENGAL");
      seaLabel(6, 80, "INDIAN OCEAN");

      MAP_MARKERS.forEach((m) => {
        const color = REGION_COLORS[m.region];
        L.circleMarker(m.coords, { radius: 6, color, weight: 1.5, fillColor: color, fillOpacity: 0.55 })
          .addTo(map)
          .bindPopup(`<b>${m.name}</b><br/>${m.status}`);
      });

      return () => { map.remove(); mapRef.current = null; };
    } catch (err) {
      setFailed(true);
      return undefined;
    }
  }, []);

  const selectRegion = (r) => {
    setActive(r);
    const v = REGION_VIEW[r.id];
    if (mapRef.current && v) mapRef.current.flyTo(v.center, v.zoom, { duration: 1.1 });
  };

  return (
    <section id="regions" data-testid="ocean-regions-section" className="relative py-24 lg:py-32 bg-oabyss/30">
      <div className="absolute inset-0 ocean-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <OceanTag index="02" label="The Expedition Regions" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-oseaglass leading-[0.95] max-w-3xl">
              One ocean. <span className="text-oreef">Many worlds.</span>
            </h2>
            <p className="max-w-md text-sm text-oseaglass/60 leading-relaxed lg:text-right">
              India's western coast, eastern coast and island territories. All sites are labelled as proposed or under
              assessment — never confirmed bookings.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 flex flex-col gap-3" data-testid="region-selector">
            {OCEAN_REGIONS.map((r) => {
              const on = active.id === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => selectRegion(r)}
                  data-testid={`region-tab-${r.id}`}
                  className={`text-left border px-5 py-4 transition-colors duration-300 ${on ? "border-oreef bg-oreef/10" : "border-oslate/20 bg-onavy/40 hover:border-oreef/40"}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] tracking-[0.25em]" style={{ color: REGION_COLORS[r.id] }}>{r.code}</span>
                    <StatusChip status={r.status} />
                  </div>
                  <div className="font-display font-semibold text-lg text-oseaglass mt-1.5 tracking-wide">{r.name}</div>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-7">
            <div className="relative border border-oreef/20 clip-corner overflow-hidden" data-testid="ocean-map-container">
              {failed ? (
                <div className="h-[360px] bg-onavy flex flex-col items-center justify-center gap-4 p-8 text-center">
                  <div className="font-mono text-xs tracking-[0.25em] text-ocoral">MAP UNAVAILABLE</div>
                  <p className="text-sm text-oseaglass/60 max-w-sm">The interactive map could not load. The expedition spans India's western and eastern coasts, Lakshadweep, and the Andaman &amp; Nicobar Islands.</p>
                </div>
              ) : (
                <div ref={containerRef} className="h-[360px] sm:h-[420px] w-full" role="application" aria-label="Interactive map of India's proposed marine expedition regions" />
              )}
              <div className="absolute bottom-3 left-3 z-[500] bg-onavy/85 backdrop-blur-sm border border-oreef/20 px-3 py-2.5 font-mono text-[9px] tracking-[0.12em] text-oseaglass/80 space-y-1.5 pointer-events-none">
                {OCEAN_REGIONS.map((r) => (
                  <div key={r.id} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: REGION_COLORS[r.id] }} /> {r.code}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Reveal delay={0.05}>
          <div key={active.id} className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 border border-oreef/15 bg-onavy/50 overflow-hidden" data-testid="region-detail">
            <div className="lg:col-span-4 h-56 lg:h-auto overflow-hidden">
              <img src={active.img} alt={active.name} className="w-full h-full object-cover" />
            </div>
            <div className="lg:col-span-8 p-7 sm:p-9">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] tracking-[0.25em]" style={{ color: REGION_COLORS[active.id] }}>{active.code}</span>
                <StatusChip status={active.status} />
              </div>
              <h3 className="font-display font-bold text-2xl sm:text-3xl text-oseaglass mt-2 tracking-wide">{active.name}</h3>
              <p className="mt-3 text-sm text-oseaglass/70 leading-relaxed max-w-2xl">{active.blurb}</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-oreef mb-2">POTENTIAL HABITATS</div>
                  <ul className="space-y-1.5">
                    {active.habitats.map((h) => <li key={h} className="text-sm text-oseaglass/70 flex gap-2"><span className="text-ocoral shrink-0">·</span>{h}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-oreef mb-2">OBSERVATION THEMES</div>
                  <ul className="space-y-1.5">
                    {active.themes.map((t) => <li key={t} className="text-sm text-oseaglass/70 flex gap-2"><span className="text-ocoral shrink-0">·</span>{t}</li>)}
                  </ul>
                </div>
              </div>
              <div className="mt-6 space-y-2">
                <p className="text-xs font-mono text-oslate leading-relaxed"><span className="text-oreef">SATELLITE DATA · </span>{active.satellite}</p>
                <p className="text-xs font-mono text-oslate leading-relaxed"><span className="text-ogold">ACCESS · </span>{active.access}</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {active.sites.map((s) => <span key={s} className="font-mono text-[10px] text-oseaglass/60 border border-oslate/20 px-2.5 py-1">{s}</span>)}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
